import { parseColor, toHex } from '../../../components/common/colorpicker/colorMath';
import { TOKEN_NAMES } from '../../../theme/themeGenerator';

/*
 * Which theme tokens paint an element in the sample swatch. Two sources are combined:
 * the var(--token) references in the CSS rules that match the element (or the ancestor
 * the value comes from), and the tokens whose value equals what the browser computed.
 * A token that appears in both is the answer; when the rules don't say (a value set
 * outside a rule, or a rule this scan can't read), the value match alone is used.
 */

const VAR_RE = /var\(\s*(--[a-z0-9-]+)/gi;

// Each part a user can see, the computed property it reads, and the declarations that can set it.
const PARTS = [
  { part: 'Text', computed: 'color', props: ['color'], inherits: true },
  { part: 'Background', computed: 'background-color', props: ['background-color', 'background'], opaqueAncestor: true },
  { part: 'Border', computed: 'border-top-color', props: ['border-color', 'border', 'border-top', 'border-top-color', 'border-bottom', 'border-bottom-color', 'border-left', 'border-left-color'], side: true },
  { part: 'Outline', computed: 'outline-color', props: ['outline', 'outline-color'], outline: true },
];

let ruleCache = null;

/** Every style rule in the page that references a custom property, flattened out of @media blocks. */
const varRules = () => {
  if (ruleCache) return ruleCache;
  const out = [];
  const walk = (rules) => {
    [...rules].forEach(r => {
      if (r.cssRules && !r.selectorText) walk(r.cssRules);
      else if (r.selectorText && r.style && r.cssText.includes('var(--')) out.push(r);
    });
  };
  [...document.styleSheets].forEach(sheet => {
    try { walk(sheet.cssRules); } catch { /* cross-origin sheet: unreadable, skipped */ }
  });
  ruleCache = out;
  return out;
};

// Selector specificity as [ids, classes/attributes/pseudo-classes, types/pseudo-elements].
// :where() counts nothing; :is() / :not() / :has() count their most specific argument.
const specificity = (sel) => {
  const spec = [0, 0, 0];
  let rest = sel.replace(/:where\((?:[^()]|\([^()]*\))*\)/g, '');
  rest = rest.replace(/:(?:is|not|has)\(((?:[^()]|\([^()]*\))*)\)/g, (m, inner) => {
    const best = inner.split(',').map(s => specificity(s.trim()))
      .reduce((a, b) => (compareSpec(a, b) >= 0 ? a : b), [0, 0, 0]);
    spec[0] += best[0]; spec[1] += best[1]; spec[2] += best[2];
    return ' ';
  });
  rest = rest.replace(/"[^"]*"|'[^']*'/g, '');
  spec[0] += (rest.match(/#[\w-]+/g) || []).length;
  spec[1] += (rest.match(/\.[\w-]+|\[[^\]]*\]|(?<!:):(?!:)[\w-]+/g) || []).length;
  spec[2] += (rest.match(/::[\w-]+|(?:^|[\s>+~])[a-zA-Z][\w-]*/g) || []).length;
  return spec;
};
const compareSpec = (a, b) => (a[0] - b[0]) || (a[1] - b[1]) || (a[2] - b[2]);

/**
 * Token names referenced by the declaration that wins for el: among matching rules that set
 * one of props, the one the cascade picks (!important, then specificity, then source order).
 * Rules that match but lose would otherwise name tokens that paint nothing here.
 */
const varsFor = (el, props) => {
  let best = null;
  varRules().forEach((r, order) => {
    let spec = null;
    try {
      r.selectorText.split(',').forEach(s => {
        if (el.matches(s)) { const sp = specificity(s.trim()); if (!spec || compareSpec(sp, spec) > 0) spec = sp; }
      });
    } catch { spec = null; }
    if (!spec) return;
    const values = props.map(p => [r.style.getPropertyValue(p), r.style.getPropertyPriority(p)]).filter(([v]) => v);
    if (values.length === 0) return;
    const important = values.some(([, pr]) => pr === 'important');
    const rank = { important, spec, order };
    const wins = !best || (rank.important !== best.rank.important ? rank.important
      : (compareSpec(rank.spec, best.rank.spec) || (rank.order - best.rank.order)) > 0);
    if (wins) best = { rank, values };
  });
  const names = new Set();
  (best?.values || []).forEach(([v]) => [...v.matchAll(VAR_RE)].forEach(m => names.add(m[1])));
  return names;
};

// Computed colors from color-mix() come back as color(srgb r g b [/ a]) with 0-1 channels,
// which parseColor does not read; everything else goes through parseColor.
const SRGB_RE = /^color\(srgb\s+([\d.e-]+)\s+([\d.e-]+)\s+([\d.e-]+)(?:\s*\/\s*([\d.e-]+%?))?\s*\)$/i;
const readColor = (str) => {
  const m = SRGB_RE.exec((str || '').trim());
  if (!m) return parseColor(str, true);
  const ch = (v) => Math.round(Math.min(1, Math.max(0, parseFloat(v))) * 255);
  const a = m[4] == null ? 1 : (m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4]));
  return { r: ch(m[1]), g: ch(m[2]), b: ch(m[3]), a };
};

const sameColor = (a, b) => a && b && a.r === b.r && a.g === b.g && a.b === b.b && Math.abs((a.a ?? 1) - (b.a ?? 1)) < 0.02;

const borderSide = (cs) => ['top', 'bottom', 'left', 'right']
  .find(s => cs.getPropertyValue(`border-${s}-style`) !== 'none' && parseFloat(cs.getPropertyValue(`border-${s}-width`)) > 0);

/**
 * [{ part, value, tokens }] for what is visible on el at rest. `root` is the preview container,
 * the boundary for walking up to inherited text and to the background that shows through.
 *
 * The element under the pointer is in its :hover state (and so are its ancestors), which would
 * report hover colors. So the swatch is read from an invisible copy beside it that no pointer
 * touches; the same child path finds the element there.
 */
export function inspectTokens(target, root, tokens) {
  if (!target || !root || !root.contains(target)) return [];
  const live = target.closest('svg') || target;
  const path = [];
  for (let n = live; n !== root; n = n.parentElement) path.unshift([...n.parentElement.children].indexOf(n));

  const copy = root.cloneNode(true);
  Object.assign(copy.style, {
    position: 'absolute', left: '-10000px', top: '0', width: `${root.offsetWidth}px`,
    visibility: 'hidden', pointerEvents: 'none',
  });
  root.parentElement.appendChild(copy);
  try {
    const el = path.reduce((n, i) => n?.children[i], copy);
    return el ? readParts(el, copy, tokens) : [];
  } finally {
    copy.remove();
  }
}

function readParts(el, root, tokens) {
  const valueMatches = (rgba) => TOKEN_NAMES.filter(n => sameColor(parseColor(tokens[n] || '', true), rgba));
  const results = [];

  PARTS.forEach(p => {
    let source = el;
    let cs = getComputedStyle(source);
    let computed = p.computed;

    if (p.side) {
      const side = borderSide(cs);
      if (!side) return;
      computed = `border-${side}-color`;
    }
    if (p.outline && (cs.outlineStyle === 'none' || !(parseFloat(cs.outlineWidth) > 0))) return;
    if (p.opaqueAncestor) {
      while (source !== root && (readColor(cs.backgroundColor)?.a ?? 0) === 0) {
        source = source.parentElement;
        cs = getComputedStyle(source);
      }
    }

    const rgba = readColor(cs.getPropertyValue(computed));
    if (!rgba || rgba.a === 0) return;

    // Inherited text: the declaring element is the nearest one with a rule that sets it.
    let declared = varsFor(source, p.props);
    if (p.inherits) {
      let up = source;
      while (declared.size === 0 && up !== root && up.parentElement) {
        up = up.parentElement;
        declared = varsFor(up, p.props);
      }
    }
    // The winning rule names the tokens; the value picks among them. A value none of them equals
    // is mixed from them (color-mix, alpha), so the rule's tokens are the answer. With no token in
    // the rule, any token of that exact value is the best guess.
    const byValue = valueMatches(rgba);
    const both = byValue.filter(n => declared.has(n));
    if (both.length) results.push({ part: p.part, value: toHex(rgba), tokens: both });
    else if (declared.size) results.push({ part: p.part, value: toHex(rgba), tokens: [...declared], mixed: true });
    else results.push({ part: p.part, value: toHex(rgba), tokens: byValue });
  });

  return results;
}
